import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../react-query.config';

// Fake delay for skeleton loading demo (remove in production)
const fakeDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const usePickemLeaderboard = ({
  pickemId,
  leagueId,
}: {
  pickemId?: string;
  leagueId?: string;
}) =>
  useInfiniteQuery({
    queryKey: queryKeys.leaderboard.pickem(pickemId, leagueId),
    queryFn: async ({ pageParam = 0 }) => {
      // Fake delay for demo (only on first page)
      if (pageParam === 0) {
        await fakeDelay(1500);
      }

      return apiClient.get('/api/leaderboard/pickem/{id}', {
        pathParams: { id: pickemId! },
        params: {
          limit: pageParam === 0 ? 100 : 50,
          offset: pageParam,
          ...(leagueId ? { league_id: leagueId } : {}),
        },
      });
    },
    getNextPageParam: lastPage => {
      // API enforces max offset of 500, so stop fetching at that limit
      const nextOffset = lastPage.offset + lastPage.limit;
      if (nextOffset >= 500) {
        return undefined; // Stop fetching at 500 entry limit
      }
      // Return the next offset if there's more data, otherwise undefined to stop fetching
      return lastPage.hasMore ? nextOffset : undefined;
    },
    initialPageParam: 0,
    enabled: !!pickemId,
    staleTime: 30000,
  });
export interface AggregateLeaderboardEntry {
  user_id: string;
  username: string;
  total_points: number;
  rank: number;
  pickems_data?: string | null;
}

type AggregateLeaderboardBaseQueryParams = {
  limit?: number;
  offset?: number;
  league_code: string;
  friends_league_id?: string;
};

type AggregateLeaderboardQueryParams =
  | (AggregateLeaderboardBaseQueryParams & {
      period: 'week';
      week_offset: number;
    })
  | (AggregateLeaderboardBaseQueryParams & {
      period: 'month';
      month: number;
      year: number;
    })
  | (AggregateLeaderboardBaseQueryParams & {
      period: 'year';
    });

export type AggregatePeriod = 'week' | 'month' | 'year';

export type AggregateLeaderboardParams =
  | { period: 'week'; weekOffset: number; leagueCode: string; friendsLeagueId?: string }
  | { period: 'month'; month: number; year: number; leagueCode: string; friendsLeagueId?: string }
  | { period: 'year'; leagueCode: string; friendsLeagueId?: string };

export const useAggregateLeaderboard = (
  params: AggregateLeaderboardParams,
  options?: { enabled?: boolean }
) =>
  useInfiniteQuery({
    queryKey: [
      'leaderboard',
      'aggregate',
      params.period,
      params.period === 'week' ? params.weekOffset : null,
      params.period === 'month' ? `${params.year}-${params.month}` : null,
      params.leagueCode,
      params.friendsLeagueId,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (pageParam === 0) {
        await fakeDelay(1500);
      }

      const baseQuery = {
        limit: 10, //pageParam === 0 ? 100 : 50,
        offset: pageParam,
        league_code: params.leagueCode,
        ...(params.friendsLeagueId ? { friends_league_id: params.friendsLeagueId } : {}),
      };

      let query: AggregateLeaderboardQueryParams;

      if (params.period === 'week') {
        query = { ...baseQuery, period: 'week', week_offset: params.weekOffset };
      } else if (params.period === 'month') {
        query = {
          ...baseQuery,
          period: 'month',
          month: params.month,
          year: params.year,
        };
      } else {
        query = { ...baseQuery, period: 'year' };
      }

      return apiClient.get('/api/leaderboards/aggregate', { params: query });
    },
    getNextPageParam: lastPage => {
      const nextOffset = lastPage.offset + lastPage.limit;
      if (nextOffset >= 500) return undefined;
      return lastPage.hasMore ? nextOffset : undefined;
    },
    initialPageParam: 0,
    staleTime: 30000,
    enabled: Boolean(params.leagueCode) && (options?.enabled ?? true),
  });

export interface UserPickemEntry {
  pickem_id: string;
  pickem_title: string;
  points: number;
  rank: number;
}

type UserPickemsQueryParams =
  | {
      period: 'today';
      league_code?: string;
    }
  | {
      period: 'week';
      week_offset?: number;
      league_code?: string;
    }
  | {
      period: 'month';
      month: number;
      year: number;
      league_code?: string;
    }
  | {
      period: 'year';
      league_code?: string;
    };

type UserPickemsPeriod = 'today' | 'week' | 'month' | 'year';

export const useUserPickemsForPeriod = (
  userId: string | undefined,
  period: UserPickemsPeriod,
  opts?: {
    weekOffset?: number;
    month?: number; // 1..12
    year?: number;
  },
  leagueCode?: string
) => {
  const now = new Date();

  const resolvedMonth = opts?.month ?? now.getMonth() + 1;
  const resolvedYear = opts?.year ?? now.getFullYear();

  const weekOffsetIsValid =
    typeof opts?.weekOffset === 'number' && Number.isFinite(opts.weekOffset);

  const enabled =
    !!userId &&
    !!leagueCode &&
    (period !== 'week' || weekOffsetIsValid) &&
    (period !== 'month' || (typeof resolvedMonth === 'number' && typeof resolvedYear === 'number'));

  return useQuery({
    queryKey: [
      'leaderboard',
      'user-pickems',
      userId,
      period,
      period === 'week' ? opts?.weekOffset : null,
      period === 'month' ? `${resolvedYear}-${resolvedMonth}` : null,
      leagueCode,
    ],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      if (!leagueCode) throw new Error('leagueCode is required');

      let params: UserPickemsQueryParams;

      if (period === 'week') {
        if (!weekOffsetIsValid) throw new Error('weekOffset is required for period=week');
        params = { period: 'week', week_offset: opts!.weekOffset, league_code: leagueCode };
      } else if (period === 'month') {
        params = {
          period: 'month',
          month: resolvedMonth,
          year: resolvedYear,
          league_code: leagueCode,
        };
      } else if (period === 'today') {
        params = { period: 'today', league_code: leagueCode };
      } else {
        params = { period: 'year', league_code: leagueCode };
      }

      return apiClient.get('/api/leaderboards/aggregate/user/{userId}/pickems', {
        pathParams: { userId },
        params,
      });
    },
    enabled,
    staleTime: 30000,
  });
};
